import { useCallback, useState } from 'react';
import useChurchConfig from './useChurchConfig';
import { printTemplates } from '../api/endpoints';
import { printDocument, NoTemplateError } from '../utils/printService';
import type { PrintData } from '../utils/printService';
import type { DocumentType, PrintTemplate } from '../types';

/**
 * Printing from somewhere a button will not fit — a row's ⋯ menu, a keyboard
 * shortcut, a confirm dialog.
 *
 * The same path as the Print button: the parish's configured template is
 * resolved, filled and opened as its own document. A screen that uses this
 * still holds no design and no print routine of its own.
 */
export interface PrintOptions {
  module: string;
  documentType: DocumentType;
  data: PrintData;
  template?: PrintTemplate;
  /** Window title, and the name the browser suggests for a PDF. */
  title?: string;
}

export const usePrint = (notify?: (message: string, type?: 'success' | 'error') => void) => {
  const config = useChurchConfig();
  const [printing, setPrinting] = useState(false);

  const print = useCallback(async (options: PrintOptions): Promise<boolean> => {
    setPrinting(true);
    try {
      await printDocument({ ...options, config });
      return true;
    } catch (err) {
      // A parish that has not configured this kind of paper is told where to
      // go rather than shown an empty window.
      notify?.(
        err instanceof NoTemplateError
          ? err.message
          : (err as Error)?.message || 'Could not produce the document.',
        'error'
      );
      return false;
    } finally {
      setPrinting(false);
    }
  }, [config, notify]);

  /** The live templates for a pair — for a screen that offers a choice. */
  const templatesFor = useCallback(
    async (module: string, documentType: DocumentType): Promise<PrintTemplate[]> => {
      try {
        const list = await printTemplates.usable(module, documentType);
        return Array.isArray(list) ? list : [];
      } catch {
        return [];
      }
    },
    []
  );

  return { print, printing, templatesFor };
};

export default usePrint;
